import { DateTime } from 'luxon';
import { CouchLoginResult } from '@cf-shared/couch-auth';
import { SubscriptionStatus } from '@cf-shared/data-model/couch-user';

// Resolve the effective "free trial ends on" date to show on the Manage
// Subscription page, as an ISO string, or undefined when the user is not
// in an active free trial right now.
//
// Why this exists: the page used to read only the top-level
// `trialEnds_utc`, which the server fills in just for PayPro trials. A
// free trial bought through Apple or Google Play keeps its trial state
// in the per-provider block (`appleData` / `googlePlayData`), so an IAP
// trial showed no trial note at all — the trial-end date looked like a
// plain renewal date. See issue #3791.
//
// Priority: Apple trial, then Google Play trial, then Paddle trial, then
// the PayPro top-level field. The IAP blocks carry their own `inTrialPeriod` flag
// (set by the validate / webhook paths) plus the per-provider
// `expiresDate_utc`, which during a free trial is the trial-end date. We
// only treat a block as a trial when `inTrialPeriod === true` AND its
// expiry is still in the future, so a normal paid renewal never shows a
// trial note and an already-finished trial is dropped.
export function resolveTrialEndIso(
  couch: CouchLoginResult | null | undefined,
  nowIso: string,
): string | undefined {
  if (!couch) return undefined;

  // if the user is in an Apple free trial, its end date wins
  if (couch.appleData?.inTrialPeriod) {
    const appleTrialEnd = futureOrUndefined(couch.appleData.expiresDate_utc, nowIso);
    if (appleTrialEnd) return appleTrialEnd;
  }

  // else if the user is in a Google Play free trial, use its end date
  if (couch.googlePlayData?.inTrialPeriod) {
    const googleTrialEnd = futureOrUndefined(couch.googlePlayData.expiresDate_utc, nowIso);
    if (googleTrialEnd) return googleTrialEnd;
  }

  // else if the user is in a Paddle free trial, use its end date. Paddle does
  // not set inTrialPeriod reliably, so an Active grant that has not counted a
  // paid charge is still a trial (mirrors the server #4184 rule). See #4189.
  const paddle = couch.paddleData;
  const isPaddleTrial = paddle?.inTrialPeriod === true ||
    (paddle?.subscriptionStatus === SubscriptionStatus.Active && paddle?.paidStartCounted !== true);
  if (isPaddleTrial) {
    const paddleTrialEnd = futureOrUndefined(paddle?.expiresDate_utc, nowIso);
    if (paddleTrialEnd) return paddleTrialEnd;
  }

  // else fall back to the PayPro top-level trial-end field
  return futureOrUndefined(couch.trialEnds_utc, nowIso);
}

// Return the date string only when it is a valid date in the future;
// otherwise undefined. A trial that already ended must not show.
function futureOrUndefined(dateIso: string | undefined, nowIso: string): string | undefined {
  if (!dateIso) return undefined;
  const date = DateTime.fromISO(dateIso);
  const now = DateTime.fromISO(nowIso);
  // a missing or unparseable date is treated as "no trial to show" — this
  // is display-only fallback, the same way populateUserDetails falls back
  // for an invalid licenseExpiry. The dates are server-written ISO fields,
  // so an invalid one would be a server data issue surfaced server-side,
  // not something this view reports.
  if (!date.isValid || !now.isValid) return undefined;
  return date > now ? dateIso : undefined;
}
